import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const SupplierPaymentsPage = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [openDialog, setOpenDialog] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    supplierId: '',
    purchaseOrderId: '',
    amount: '',
    paymentMethod: 'BANK_TRANSFER',
    reference: '',
    notes: ''
  });

  const fetchPayments = async () => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl('supplier-payments'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load supplier payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl('suppliers'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(res.data);
    } catch (err) {
      console.error('Failed to fetch suppliers', err);
    }
  };

  const fetchPurchaseOrders = async (supplierId: string) => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl('purchases'), {
        params: { supplierId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseOrders(res.data.filter((po: any) => po.status !== 'PAID'));
    } catch (err) {
      console.error('Failed to fetch purchase orders', err);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({
      supplierId: '',
      purchaseOrderId: '',
      amount: '',
      paymentMethod: 'BANK_TRANSFER',
      reference: '',
      notes: ''
    });
  };

  const handleSupplierChange = (supplierId: string) => {
    setFormData({ ...formData, supplierId, purchaseOrderId: '' });
    fetchPurchaseOrders(supplierId);
  };

  const handlePOChange = (poId: string) => {
    const selectedPO = purchaseOrders.find(po => po.id === poId);
    if (selectedPO) {
      const totalPaid = selectedPO.payments?.reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
      const balance = Number(selectedPO.totalAmount) - totalPaid;
      setFormData({ ...formData, purchaseOrderId: poId, amount: String(balance.toFixed(2)) });
    } else {
      setFormData({ ...formData, purchaseOrderId: poId });
    }
  };

  const handleRecordPayment = async () => {
    if (!formData.supplierId || !formData.purchaseOrderId || !formData.amount) {
      alert('Please fill in required fields (Supplier, Purchase Order, and Amount)');
      return;
    }

    setSubmitting(true);
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      await axios.post(getApiUrl('supplier-payments'), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDialog(false);
      fetchPayments();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Supplier Payments</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}
        >
          Record Payment
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell>Payment No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Purchase Order</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>{payment.paymentNo}</TableCell>
                  <TableCell>{new Date(payment.paymentDate).toLocaleDateString()}</TableCell>
                  <TableCell>{payment.supplier?.name}</TableCell>
                  <TableCell>{payment.purchaseOrder?.orderNo || 'Direct'}</TableCell>
                  <TableCell>{payment.paymentMethod}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>${Number(payment.amount).toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={payment.status} 
                      color={payment.status === 'COMPLETED' ? 'success' : 'warning'} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No supplier payments recorded
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Record Supplier Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Select Supplier"
                value={formData.supplierId}
                onChange={(e) => handleSupplierChange(e.target.value)}
                required
              >
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name} (Bal: ${Number(s.balance).toFixed(2)})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Related Purchase Order"
                value={formData.purchaseOrderId}
                onChange={(e) => handlePOChange(e.target.value)}
                disabled={!formData.supplierId}
                required
              >
                {purchaseOrders.map((po) => {
                  const totalPaid = po.payments?.reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
                  const balance = Number(po.totalAmount) - totalPaid;
                  return (
                    <MenuItem key={po.id} value={po.id}>
                      {po.orderNo} - Total: ${Number(po.totalAmount).toFixed(2)} (Bal: ${balance.toFixed(2)})
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Payment Method"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                <MenuItem value="ECOCASH">Ecocash</MenuItem>
                <MenuItem value="CHECK">Check</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Reference / Transaction ID"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRecordPayment} 
            variant="contained" 
            disabled={submitting}
            sx={{ bgcolor: '#ffab00', color: '#1a237e', fontWeight: 'bold', '&:hover': { bgcolor: '#ffd740' } }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierPaymentsPage;
